import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import ServiceProviderCalendar from "@/components/features/ServiceProviderCalendar/ServiceProviderCalendar";
import { TimeOffRequestsLog } from "@/components/features/ServiceProviderCalendar/TimeOffRequestsLog/TimeOffRequestsLog";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import { usePersistedTabs } from "@/hooks/usePersistedTabs";
import type { TabItemType } from "@/types/common.types";
import { spCalendarSearchSchema } from "@/hooks/useCalendarFilterPersistence";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { createFileRoute } from "@tanstack/react-router";
import { Box, Flex, Group, Tabs, Title } from "@mantine/core";

import styles from "./sp-calendar.module.scss";

export const Route = createFileRoute("/_authenticated/sp-calendar/")({
  component: SPCalendarPage,
  validateSearch: spCalendarSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const tabsData: TabItemType[] = [
  {
    value: "schedule",
    label: "Schedule",
    component: (
      <Box className={styles.calendarWrapper}>
        <ServiceProviderCalendar />
      </Box>
    ),
  },
  {
    value: "time-off-log",
    label: "Time-off Requests Log",
    component: (
      <Box className={styles.calendarWrapper}>
        <TimeOffRequestsLog />
      </Box>
    ),
  },
];

function SPCalendarPage() {
  const { activeTab, setActiveTab } = usePersistedTabs({
    defaultTab: "schedule",
    queryParamName: "tab",
  });

  useBreadcrumb(
    [
      {
        title: "SP Calendar",
        href: "/sp-calendar",
        icon: getRouteIcon(MODULE_KEYS_MAP.spCalendar),
      },
    ],
    { listView: "Service Provider Calendar" },
  );

  return (
    <Flex direction="column" className={styles.container}>
      <Group className={styles.header}>
        <Title order={3} className={styles.title}>
          Service Provider Calendar
        </Title>
      </Group>

      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab(value ?? "schedule")}
        className={styles.tab}
        style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      >
        <Tabs.List className={styles.tabsList}>
          {tabsData.map((tab) => (
            <Tabs.Tab key={tab.value} value={tab.value} className={styles.tab}>
              {tab.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {tabsData.map((tab) => (
          <Tabs.Panel key={tab.value} value={tab.value} className={styles.tabPanel}>
            {activeTab === tab.value && tab.component}
          </Tabs.Panel>
        ))}
      </Tabs>
    </Flex>
  );
}
